/* =====================================================================
 * MOSTLY INERT — KEPT ON PURPOSE.
 *
 * Its TYPES are still imported by api/tests-api.ts (the question rows
 * are still returned by the API and still stored), and its unit tests
 * still run. Its VALIDATION AND RENDERING HELPERS are unused: nothing
 * calls them because nothing renders a dynamic question any more.
 *
 * The dynamic question engine. Step one of a sitting used to be built
 * from an admin-editable question pool; it is now the fixed
 * CandidateApplicationForm, which saves to its own table.
 *
 * This file is COMPLETE AND WORKING, not a broken remnant. It is kept
 * because the pool has a real future use — a second, exam-specific
 * question set alongside the application — and rewriting it from
 * scratch later would be strictly worse than un-commenting it.
 *
 * See CLAUDE.md, "Inert code kept on purpose", for the full file list
 * and what to re-wire.
 * ===================================================================== */

/**
 * What a question is, when it counts as answered, and what is wrong with
 * an answer that is not.
 *
 * ONE DEFINITION, used by the field renderer, the progress bar and the
 * Continue button alike. They were three separate judgements once and it
 * showed: a progress bar that says "12 of 12" above a button that
 * refuses to enable is a bug report the candidate cannot write, because
 * from the outside the two are simply contradicting each other.
 *
 * The shapes an answer takes, by kind:
 *
 *   TEXT LONG_TEXT EMAIL DATE NUMBER   string
 *   SINGLE_CHOICE                      string
 *   MULTI_CHOICE                       string[]
 *   SCALE                              Record<rowId, number>
 *   NUMBER_GRID                        Record<rowId, number | null>
 *
 * The last two are why this file exists rather than a one-line
 * `String(value).trim()`. `String({})` is `"[object Object]"`, which is
 * not empty -- so an untouched scale would have counted as answered, and
 * a required one would have waved the candidate straight past it.
 */

import type { SnapshotQuestion } from "@/api/tests-api";

export type QuestionKind =
  | "TEXT"
  | "LONG_TEXT"
  | "EMAIL"
  | "DATE"
  | "NUMBER"
  | "SINGLE_CHOICE"
  | "MULTI_CHOICE"
  | "SCALE"
  | "NUMBER_GRID"
  /** Answered by speaking to camera against a clock. No field, no value
   *  to validate: the take IS the answer. Only ever used in a video
   *  round, and its allowance lives in `config.allotted_seconds`. */
  | "VIDEO";

export interface GridRow {
  id: string;
  label: string;
  hint?: string;
}

export interface QuestionConfig {
  min?: number;
  max?: number;
  /** The scale legend, e.g. "1 never used · 5 fully fluent". */
  key?: string;
  /** Suffix beside a NUMBER_GRID input, e.g. "/day". */
  unit?: string;
  /** Show a computed total under a NUMBER_GRID. */
  total?: boolean;
  rows?: GridRow[];
  /** VIDEO only: how long the candidate has to answer, in seconds.
   *  Required by the server, bounded to 15s..15min. For a round-2
   *  question the server DERIVES it: thinking + answering + the 5s
   *  "time's up" beat. */
  allotted_seconds?: number;
  /** Round 2: the question's own heading, e.g. "The complaint". */
  title?: string;
  /** Round 2: thinking time before the answer, 0..600 seconds. */
  think_seconds?: number;
  /** Round 2: answer time, 15..900 seconds. Its presence is what makes
   *  a question round 2's shape. */
  answer_seconds?: number;
  /** Round 2: asked first and not scored. */
  warmup?: boolean;
}

/** Which round a question belongs to, and which round an exam runs as. */
export type Round = "ROUND_1" | "VIDEO_ROUND";

export const ROUND_LABELS: Record<Round, string> = {
  ROUND_1: "Round 1",
  VIDEO_ROUND: "Video round",
};

export const rowsOf = (q: SnapshotQuestion): GridRow[] =>
  (q.config?.rows ?? []).filter((r): r is GridRow => !!r && !!r.id);

/** Scale points, e.g. [1,2,3,4,5]. */
export function scalePoints(q: SnapshotQuestion): number[] {
  const lo = q.config?.min ?? 1;
  const hi = q.config?.max ?? 5;
  const out: number[] = [];
  for (let n = lo; n <= hi; n++) out.push(n);
  return out;
}

/**
 * Is there an answer here?
 *
 * Deliberately strict about blanks: a space is not an answer, and the
 * server agrees. For the grid kinds EVERY row must be filled — a
 * half-completed confidence matrix is not a partial answer to one
 * question, it is an unanswered question about the rows left blank.
 */
export function isAnswered(q: SnapshotQuestion, value: unknown): boolean {
  const kind = q.kind as QuestionKind;

  if (kind === "MULTI_CHOICE") {
    return Array.isArray(value) && value.length > 0;
  }

  if (kind === "SCALE" || kind === "NUMBER_GRID") {
    const rows = rowsOf(q);
    if (rows.length === 0) return false;
    const map = (value ?? {}) as Record<string, unknown>;
    if (typeof map !== "object") return false;
    return rows.every((r) => {
      const v = map[r.id];
      return v !== undefined && v !== null && v !== "";
    });
  }

  return String(value ?? "").trim() !== "";
}

// Deliberately permissive, and that is the point: this runs while
// somebody is still typing. It rejects what is obviously not an address
// rather than trying to be the authority on what is — the server and the
// mail provider are that, and a regex that refuses a real address is
// worse than one that accepts a fake.
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * What is wrong with this answer, or null if nothing is.
 *
 * Returns a message for the candidate, not a code. `touched` exists so a
 * field the candidate has not reached yet is never shown in red: an
 * untouched required field is incomplete, which the progress bar says
 * calmly, not an error, which the field would say accusingly.
 */
export function validate(
  q: SnapshotQuestion,
  value: unknown,
  touched: boolean,
): string | null {
  const kind = q.kind as QuestionKind;
  const filled = isAnswered(q, value);

  if (!filled) {
    if (q.required && touched) {
      if (kind === "SCALE") return "Rate every row";
      if (kind === "NUMBER_GRID") return "Enter a number for every row";
      if (kind === "MULTI_CHOICE") return "Choose at least one";
      return "Required";
    }
    return null;
  }

  if (kind === "EMAIL" && !EMAIL_RE.test(String(value).trim())) {
    return "Enter a valid email address";
  }

  if (kind === "NUMBER") {
    const n = Number(String(value));
    if (!Number.isFinite(n)) return "Enter a number";
    const { min, max } = q.config ?? {};
    if (min != null && n < min) return `Must be ${min} or more`;
    if (max != null && n > max) return `Must be ${max} or less`;
  }

  return null;
}

/** Required questions still without an answer. */
export const outstanding = (
  questions: SnapshotQuestion[],
  answers: Record<string, unknown>,
): SnapshotQuestion[] =>
  questions.filter((q) => q.required && !isAnswered(q, answers[q.id]));

/** Every question carrying a real error right now. */
export const errored = (
  questions: SnapshotQuestion[],
  answers: Record<string, unknown>,
  touched: Record<string, boolean>,
): SnapshotQuestion[] =>
  questions.filter((q) => validate(q, answers[q.id], !!touched[q.id]) !== null);

export interface QuestionSection {
  code: string | null;
  title: string | null;
  hint: string | null;
  questions: SnapshotQuestion[];
}

/**
 * Group a flat snapshot into the sections it was built as.
 *
 * ORDER COMES FROM THE SNAPSHOT, never from sorting by code. The admin
 * chose the sequence and the seal froze it; re-sorting here would show a
 * candidate a different form from the one that was built, and a later
 * reviewer a different one again.
 *
 * Questions with no section fall into a single leading unlabelled group,
 * which is exactly what every question written before sections existed
 * is.
 */
export function intoSections(questions: SnapshotQuestion[]): QuestionSection[] {
  const sections: QuestionSection[] = [];
  for (const q of questions) {
    const code = q.section_code ?? null;
    const title = q.section_title ?? null;
    const last = sections[sections.length - 1];
    if (last && last.code === code && last.title === title) {
      last.questions.push(q);
      continue;
    }
    sections.push({
      code,
      title,
      hint: q.section_hint ?? null,
      questions: [q],
    });
  }
  return sections;
}
